import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Coins, Info, Undo2, Wallet } from 'lucide-react';
import clsx from 'clsx';
import type { IncentivePostResponse, IncentivePreviewLine, IncentivePreviewResponse } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { formatDate, formatINR } from '../lib/format';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { KpiStrip } from '../components/shared/KpiStrip';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { cycleLabel, type SalaryCycle } from '../components/finance/types';
import { ReasonDrawer } from '../components/teacher-reports/review/ReasonDrawer';
import { academyMonth, formatMonth } from '../components/teacher-reports/format';
import { isReportsDisabled } from '../components/teacher-reports/useDueReports';

export const Route = createFileRoute('/_authed/finance/incentives')({
  component: IncentivesPage,
});

const card = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]';
const pill = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap';

function IncentivesPage() {
  const canPost = useAuthStore((s) => s.hasRole('super_admin', 'finance_admin'));
  if (!canPost) {
    return (
      <ListShell title="Report incentives">
        <EmptyState
          title="Finance only"
          description="Finance admins and super admins post reporting incentives to salary cycles."
          icon={<Coins size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <Incentives />;
}

const monthKey = (c: Pick<SalaryCycle, 'year' | 'month'>) => `${c.year}-${String(c.month).padStart(2, '0')}`;

function Incentives() {
  const isSuperAdmin = useAuthStore((s) => s.hasRole('super_admin'));
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);

  const cycles = useQuery({
    queryKey: ['salaries', 'cycles'] as const,
    queryFn: ({ signal }) => apiRequest<{ items: SalaryCycle[] }>('/api/salaries/cycles', { signal }),
  });
  // Default to this month's cycle — it pays last month's points.
  useEffect(() => {
    if (cycleId || !cycles.data?.items.length) return;
    const now = academyMonth();
    setCycleId((cycles.data.items.find((c) => monthKey(c) === now) ?? cycles.data.items[0]!).id);
  }, [cycles.data, cycleId]);

  const preview = useQuery({
    queryKey: ['teacher-reports', 'incentives', cycleId] as const,
    enabled: !!cycleId,
    queryFn: ({ signal }) =>
      apiRequest<IncentivePreviewResponse>('/api/teacher-reports/incentives/preview', { signal, query: { salaryCycleId: cycleId! } }),
    retry: (count, err) => !isReportsDisabled(err) && count < 2,
  });
  const d = preview.data;
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['teacher-reports', 'incentives'] });
    void queryClient.invalidateQueries({ queryKey: ['salaries'] });
    // The salary screens' own caches — posting and reversing change their amounts.
    void queryClient.invalidateQueries({ queryKey: ['salaryCycles'] });
    void queryClient.invalidateQueries({ queryKey: ['salaryPayments'] });
    void queryClient.invalidateQueries({ queryKey: ['salary-cycle'] });
    void queryClient.invalidateQueries({ queryKey: ['salary-payment'] });
    void queryClient.invalidateQueries({ queryKey: ['salary-payslip'] });
  };

  const post = useMutation({
    mutationFn: () => apiRequest<IncentivePostResponse>('/api/teacher-reports/incentives/post', { method: 'POST', body: { salaryCycleId: cycleId } }),
    onSuccess: (r) => {
      // The reply counts one line per teacher per month earned — name the teachers
      // only when the posting matched the preview (nothing changed in between).
      const teachers = d && r.lines === payable.length ? d.totals.teachers : null;
      toast(
        teachers !== null
          ? `Posted ${formatINR(r.totalPaise)} for ${teachers} teacher${teachers === 1 ? '' : 's'}.`
          : `Posted ${formatINR(r.totalPaise)} to the salaries.`,
        { variant: 'success' },
      );
      invalidate();
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not post incentives.', { variant: 'error' }),
  });
  const reverse = useMutation({
    mutationFn: (v: { id: string; reason: string }) =>
      apiRequest(`/api/teacher-reports/incentives/${encodeURIComponent(v.id)}/reverse`, { method: 'POST', body: { reason: v.reason } }),
    onSuccess: () => {
      toast('Posting reversed — its points are free to post again.', { variant: 'success' });
      setReversing(null);
      invalidate();
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not reverse the posting.', { variant: 'error' }),
  });

  const cycle = d?.cycle;
  const label = cycle ? cycleLabel(cycle.year, cycle.month) : '';
  const paysMonth = d ? formatMonth(shiftBack(d.paysBefore)) : '';
  const payable = useMemo(() => (d?.lines ?? []).filter((l) => !l.problem), [d]);

  const onPost = async () => {
    if (!d) return;
    const ok = await confirm({
      title: `Add incentives to the ${label} salaries?`,
      description: `${formatINR(d.totals.amountPaise)} for ${d.totals.teachers} teacher${d.totals.teachers === 1 ? '' : 's'} (${d.totals.points} points). Each gets a “Reporting incentive” line on their ${label} salary.`,
      confirmLabel: 'Post incentives',
    });
    if (ok) post.mutate();
  };

  return (
    <ListShell
      title="Report incentives"
      description="Banked points become a “Reporting incentive” line on each teacher’s salary. Only approved points are paid — pending points are never lost."
      primaryAction={
        cycles.data?.items.length ? (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-[color:var(--color-text-muted)]">Salary cycle</span>
            <select
              className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-2 py-1.5 text-sm focus:border-gold-500 focus:outline-none"
              value={cycleId ?? ''}
              onChange={(e) => setCycleId(e.target.value)}
            >
              {cycles.data.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {cycleLabel(c.year, c.month)}
                </option>
              ))}
            </select>
          </label>
        ) : null
      }
    >
      <div className="space-y-4">
        {cycles.isLoading || (cycleId && preview.isLoading) ? (
          <div className="grid gap-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : cycles.data && cycles.data.items.length === 0 ? (
          <EmptyState
            title="No salary cycles yet"
            description="Create a salary cycle first — incentives are added to its salaries."
            icon={<Wallet size={32} aria-hidden="true" />}
          />
        ) : preview.isError || cycles.isError ? (
          isReportsDisabled(preview.error) ? (
            <EmptyState title="Teacher reports are switched off" icon={<Coins size={32} aria-hidden="true" />} />
          ) : (
            <ErrorState
              title={cycles.isError ? 'Could not load the salary cycles' : 'Could not load incentives'}
              description={
                cycles.isError
                  ? cycles.error instanceof ApiError
                    ? cycles.error.message
                    : undefined
                  : preview.error instanceof ApiError
                    ? preview.error.message
                    : undefined
              }
              onRetry={() => void (cycles.isError ? cycles.refetch() : preview.refetch())}
            />
          )
        ) : d && cycle ? (
          <>
            <KpiStrip
              className="lg:grid-cols-4!"
              items={[
                { label: 'Points to post', value: d.totals.points.toLocaleString('en-IN'), sub: `Banked, earned before ${formatMonth(d.paysBefore).split(' ')[0]}` },
                { label: 'Incentive total', value: <span className="text-[color:var(--color-success)]">{formatINR(d.totals.amountPaise)}</span>, sub: `${formatINR(d.ratePaise)} per point · up to ${formatINR(d.capPaise)} a month` },
                {
                  label: 'Still pending',
                  value: <span className={clsx(d.pendingPoints > 0 && 'text-[color:var(--color-warning)]')}>{d.pendingPoints}</span>,
                  sub: 'Waiting for approval — paid later',
                },
                { label: 'Cycle', value: label, sub: cycle.isHeld ? 'On hold' : `Pays ${formatDate(cycle.payoutDate)}` },
              ]}
            />

            <section className={clsx(card, 'border-gold-500/60 px-4 py-3')}>
              <header className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Wallet size={16} aria-hidden="true" /> Month-end payout
                </h2>
                <Link to="/finance/salaries/$cycleId" params={{ cycleId: cycle.id }} className="text-xs font-medium text-gold-700 hover:underline dark:text-gold-300">
                  Open {label} salaries →
                </Link>
              </header>
              <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
                Pays points earned in {paysMonth} and earlier that haven’t been paid yet. Each teacher’s month is capped at {formatINR(d.capPaise)}.
              </p>

              {d.lines.length === 0 ? (
                <p className="py-4 text-sm text-[color:var(--color-text-muted)]">No banked points waiting to be paid.</p>
              ) : (
                <>
                  <table className="mt-3 hidden w-full text-sm md:table">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
                      <tr className="border-b border-[color:var(--color-border)]">
                        <th className="py-2 font-semibold">Teacher</th>
                        <th className="py-2 font-semibold">Earned</th>
                        <th className="py-2 text-right font-semibold">Points</th>
                        <th className="py-2 text-right font-semibold">Paid before</th>
                        <th className="py-2 text-right font-semibold">Incentive</th>
                        <th className="py-2 pl-4 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dashed divide-[color:var(--color-border)]">
                      {d.lines.map((l) => (
                        <tr key={`${l.teacherId}-${l.earnedMonth}`} className={clsx(l.problem && 'text-[color:var(--color-text-muted)]')}>
                          <td className="py-2 font-semibold">{l.teacherName}</td>
                          <td className="py-2">{formatMonth(l.earnedMonth)}</td>
                          <td className="py-2 text-right tabular-nums">{l.points}</td>
                          <td className="py-2 text-right tabular-nums">{l.alreadyPaidPaise ? formatINR(l.alreadyPaidPaise) : '–'}</td>
                          <td className="py-2 text-right font-bold tabular-nums">{formatINR(l.amountPaise)}</td>
                          <td className="py-2 pl-4">
                            <LineStatus line={l} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ul className="mt-2 divide-y divide-dashed divide-[color:var(--color-border)] md:hidden">
                    {d.lines.map((l) => (
                      <li key={`${l.teacherId}-${l.earnedMonth}`} className="flex items-start gap-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold">{l.teacherName}</p>
                          <p className="text-xs text-[color:var(--color-text-muted)]">
                            {formatMonth(l.earnedMonth)} · {l.points} points{l.alreadyPaidPaise ? ` · ${formatINR(l.alreadyPaidPaise)} paid before` : ''}
                          </p>
                          <div className="mt-1">
                            <LineStatus line={l} />
                          </div>
                        </div>
                        <b className="shrink-0 tabular-nums">{formatINR(l.amountPaise)}</b>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="mt-3 flex items-start gap-2 rounded-md bg-[color:var(--color-surface-strong)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
                <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  <b>Pending points</b> stay in the month they were earned. Once approved they’re paid in the next posting as a separate “(approved late)” line,
                  within that month’s cap — never rolled into a new month. Teachers without a salary record keep their points until one is added.
                </span>
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                {cycle.isHeld ? <span className="text-xs text-[color:var(--color-warning)]">This cycle is on hold.</span> : null}
                <Button
                  icon={<Wallet size={16} aria-hidden="true" />}
                  disabled={payable.length === 0 || cycle.isHeld}
                  loading={post.isPending}
                  onClick={() => void onPost()}
                >
                  Add to {label} salaries
                </Button>
              </div>
            </section>

            {d.postings.length > 0 ? (
              <section className={clsx(card, 'px-4 py-3')}>
                <h2 className="text-sm font-semibold">Postings to {label}</h2>
                <ul className="mt-1 divide-y divide-dashed divide-[color:var(--color-border)]">
                  {d.postings.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">
                          {formatINR(p.totalPaise)} · {p.totalPoints} points
                        </p>
                        <p className="text-xs text-[color:var(--color-text-muted)]">
                          {formatDate(p.postedAt, { withTime: true })}
                          {p.postedByName ? ` · by ${p.postedByName}` : ''}
                          {p.status === 'reversed' && p.reverseReason ? ` · reversed: ${p.reverseReason}` : ''}
                        </p>
                      </div>
                      {p.status === 'reversed' ? (
                        <span className={clsx(pill, 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]')}>Reversed</span>
                      ) : (
                        <span className={clsx(pill, 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]')}>Posted</span>
                      )}
                      {isSuperAdmin && p.reversible ? (
                        <Button variant="secondary" size="sm" icon={<Undo2 size={14} aria-hidden="true" />} onClick={() => setReversing(p.id)}>
                          Reverse
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </div>

      <ReasonDrawer
        open={reversing !== null}
        title="Reverse this posting"
        intro="The incentive lines come off the salaries and the points go back to banked, ready for another posting. Only possible while none of these salaries are paid."
        label="Why is it being reversed?"
        submitLabel="Reverse posting"
        maxLength={500}
        danger
        busy={reverse.isPending}
        onClose={() => setReversing(null)}
        onSubmit={(reason) => reversing && reverse.mutate({ id: reversing, reason })}
      />
    </ListShell>
  );
}

/** "2026-10" → "2026-09" */
function shiftBack(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function LineStatus({ line: l }: { line: IncentivePreviewLine }) {
  if (l.problem === 'missing_payee') {
    return (
      <span className={clsx(pill, 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]')}>
        <AlertTriangle size={11} aria-hidden="true" /> No salary record — kept for later
      </span>
    );
  }
  if (l.problem === 'payment_paid') {
    return (
      <span className={clsx(pill, 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]')}>
        <AlertTriangle size={11} aria-hidden="true" /> Salary already paid — next cycle
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span className={clsx(pill, 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]')}>Ready</span>
      {l.late ? <span className={clsx(pill, 'bg-gold-500/15 text-gold-700 dark:text-gold-300')}>Approved late</span> : null}
      {l.capped ? <span className={clsx(pill, 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]')}>Capped</span> : null}
    </span>
  );
}
